/**
 * KK-101 / KK-108 · Kişiler (öğrenci ve personel).
 *
 * Listede fotoğraf URL'si BİLEREK yoktur; sunucu yalnızca "foto_var" döner
 * (ADR-17 / KK-536, §12.3). Fotoğrafı olmayan kişi bir HATA DEĞİLDİR: baş harf
 * avatarı çizilir, düzen bozulmaz, uyarı gösterilmez.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Avatar,
  BosDurum,
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  TelefonAlani,
  Tutar,
  Uyari,
} from "@kantin/ui";
import {
  CuzdanTuru,
  HAREKET_TIPI_ADI,
  KartDurumu,
  KisiTuru,
  KisitTipi,
  TELEFON_ORNEK,
  telefonGoster,
  telefonNormalize,
} from "@kantin/shared";
import { apiOku, apiYaz } from "./api.js";
import { FotografSecici } from "./FotografSecici.js";
import { ParolaAlani, VeliParolasi } from "./VeliParolasi.js";
import { useMedyaGoruntusu } from "../../veri/medya.js";
import {
  AramaKutusu,
  IslemDurumu,
  MetinAlani,
  OnayKutusu,
  Sayfa,
  SayfaBasligi,
  Sayfalayici,
  SirKopyala,
  SorguDurumu,
  TehlikeliOnay,
  tarihSaatGoster,
} from "./ortak.js";

// ---------------------------------------------------------------------------
// Tipler — apps/api/src/moduller/kisi.rotalari.ts yanıtları
// ---------------------------------------------------------------------------

interface KisiSatiri {
  id: string;
  ad: string;
  soyad: string;
  sinif: string | null;
  numara: string | null;
  tur: number;
  aktif: boolean;
  yukleme_referansi: string;
  foto_var: boolean;
  bakiye: string;
  aktif_kart_id: string | null;
  aktif_kart_uid: string | null;
}

interface KisiListesi {
  sayfa: number;
  boyut: number;
  toplam: number;
  veri: KisiSatiri[];
}

interface KisiDetayi {
  id: string;
  ad: string;
  soyad: string;
  tur: number;
  sinif: string | null;
  numara: string | null;
  kimlik_no: string | null;
  /** Düz metin: okul veliye iletir, veli tekrar sorarsa buradan okunur. */
  parola: string | null;
  foto_url: string | null;
  foto_medya_id: string | null;
  foto_rizasi: boolean;
  alerji_notu: string | null;
  yukleme_referansi: string;
  aktif: boolean;
  cuzdanlar: {
    id: string;
    tur: number;
    bakiye: string;
    eksi_tolerans: string;
  }[];
  kartlar: {
    id: string;
    uid: string;
    durum: number;
    depozito: string;
    bloke_zamani: string | null;
    bloke_sebebi: string | null;
    teslim_zamani: string | null;
  }[];
  son_hareketler: {
    id: string;
    cuzdan_id: string;
    tutar: string;
    tip: number;
    aciklama: string | null;
    nokta_ad: string | null;
    olusma_zamani: string;
  }[];
  kisitlar: {
    id: string;
    tip: number;
    urun_id: string | null;
    kategori_id: string | null;
    alerjen: string | null;
    deger: string | null;
    kaynak: number;
    aktif: boolean;
  }[];
  veliler: {
    id: string;
    ad: string;
    soyad: string;
    /** 0027: kimlik no ile ilk girişte kurulan hesabın telefonu yoktur. */
    telefon: string | null;
    eposta: string | null;
    yakinlik: string | null;
  }[];
}

const KISI_TURU_ADI: Record<number, string> = {
  [KisiTuru.OGRENCI]: "Öğrenci",
  [KisiTuru.PERSONEL]: "Personel",
  [KisiTuru.MISAFIR]: "Misafir",
};

const CUZDAN_ADI: Record<number, string> = {
  [CuzdanTuru.KANTIN]: "Kantin",
  [CuzdanTuru.YEMEKHANE]: "Yemekhane",
  [CuzdanTuru.DEPOZITO]: "Depozito",
};

const KART_DURUM_ADI: Record<number, string> = {
  [KartDurumu.AKTIF]: "Aktif",
  [KartDurumu.BLOKE]: "Bloke",
  [KartDurumu.IADE]: "İade edildi",
  [KartDurumu.HAVUZDA]: "Havuzda",
};

const KISIT_ADI: Record<number, string> = {
  [KisitTipi.GUNLUK_LIMIT]: "Günlük limit",
  [KisitTipi.URUN_YASAK]: "Ürün yasağı",
  [KisitTipi.KATEGORI_YASAK]: "Kategori yasağı",
  [KisitTipi.ALERJEN_YASAK]: "Alerjen yasağı",
  [KisitTipi.GUNLUK_ADET]: "Günlük adet",
};

const BOYUT = 25;

// ---------------------------------------------------------------------------

export function Kisiler() {
  const [arama, setArama] = useState("");
  const [sinif, setSinif] = useState("");
  const [tur, setTur] = useState("");
  const [aktif, setAktif] = useState("true");
  const [sayfa, setSayfa] = useState(1);
  const [secili, setSecili] = useState<string | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);

  const sorgu = useQuery({
    queryKey: ["kisi", arama, sinif, tur, aktif, sayfa],
    queryFn: () =>
      apiOku<KisiListesi>("/kisi", {
        arama: arama || undefined,
        sinif: sinif || undefined,
        tur: tur || undefined,
        aktif: aktif || undefined,
        sayfa,
        boyut: BOYUT,
      }),
  });

  const satirlar = sorgu.data?.veri ?? [];

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Kişiler"
        aciklama="Öğrenci ve personel kayıtları. Cüzdan kişiye aittir, karta değil — kart değişse de bakiye kişidedir."
        eylem={
          <Buton
            tur="birincil"
            onClick={() => {
              setYeniAcik((a) => !a);
              setSecili(null);
            }}
            aria-expanded={yeniAcik}
          >
            {yeniAcik ? "Formu kapat" : "Yeni kişi"}
          </Buton>
        }
      />

      {yeniAcik && (
        <YeniKisiFormu
          kapat={() => setYeniAcik(false)}
          eklendi={() => {
            setYeniAcik(false);
            void sorgu.refetch();
          }}
        />
      )}

      <Kart
        baslik="Arama ve süzgeçler"
        ustBilgi={
          <span className="text-sm text-murekkep-3">
            Ad, soyad, numara veya referans kodu ile aranır
          </span>
        }
      >
        <div className="grid gap-3 md:grid-cols-4">
          <AramaKutusu
            etiket="Ara"
            ipucu="Ayşe Yılmaz / 1042 / KNT-4821"
            deger={arama}
            degistir={(d) => {
              setArama(d);
              setSayfa(1);
            }}
          />
          <Alan
            etiket="Sınıf"
            value={sinif}
            onChange={(e) => {
              setSinif(e.target.value);
              setSayfa(1);
            }}
            placeholder="5-A"
            ipucu="Boş bırakılırsa tüm sınıflar"
          />
          <Secim
            etiket="Tür"
            value={tur}
            onChange={(e) => {
              setTur(e.target.value);
              setSayfa(1);
            }}
            secenekler={[
              { deger: "", metin: "Hepsi" },
              { deger: String(KisiTuru.OGRENCI), metin: "Öğrenci" },
              { deger: String(KisiTuru.PERSONEL), metin: "Personel" },
              { deger: String(KisiTuru.MISAFIR), metin: "Misafir" },
            ]}
          />
          <Secim
            etiket="Durum"
            value={aktif}
            onChange={(e) => {
              setAktif(e.target.value);
              setSayfa(1);
            }}
            secenekler={[
              { deger: "true", metin: "Aktif" },
              { deger: "false", metin: "Pasif" },
              { deger: "", metin: "Hepsi" },
            ]}
          />
        </div>
      </Kart>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <section aria-label="Kişi listesi">
          <SorguDurumu
            yukleniyor={sorgu.isPending}
            hata={sorgu.error}
            bos={satirlar.length === 0}
            bosBaslik="Kişi bulunamadı"
            bosAciklama="Süzgeçleri genişletin veya yeni kişi ekleyin. Toplu kayıt için İçe Aktarım ekranını kullanın."
            yenile={() => void sorgu.refetch()}
          >
            <Tablo>
              <TabloBaslik>
                <th scope="col">Kişi</th>
                <th scope="col">Sınıf / No</th>
                <th scope="col">Referans</th>
                <th scope="col">Kart</th>
                <th scope="col">Bakiye</th>
                <th scope="col">
                  <span className="gizli-erisilebilir">İşlem</span>
                </th>
              </TabloBaslik>
              <TabloGovde>
                {satirlar.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        {/* KK-536: fotoğraf yoksa baş harf avatarı. */}
                        <Avatar ad={k.ad} soyad={k.soyad} boyut={36} />
                        <div>
                          <p className="m-0 font-semibold text-murekkep">
                            {k.ad} {k.soyad}
                          </p>
                          <p className="m-0 text-xs text-murekkep-3">
                            {KISI_TURU_ADI[k.tur] ?? "Bilinmiyor"}
                            {k.foto_var ? " · fotoğraflı" : ""}
                            {!k.aktif ? " · pasif" : ""}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>
                      {k.sinif ?? "—"}
                      {k.numara && (
                        <span className="rakam block font-mono text-xs text-murekkep-3">
                          No {k.numara}
                        </span>
                      )}
                    </td>
                    <td className="rakam font-mono text-xs">
                      {k.yukleme_referansi}
                    </td>
                    <td>
                      {k.aktif_kart_uid ? (
                        <span className="rakam font-mono text-xs">
                          {k.aktif_kart_uid}
                        </span>
                      ) : (
                        <Rozet tur="notr">kart yok</Rozet>
                      )}
                    </td>
                    <td className="text-right">
                      <Tutar deger={k.bakiye} yonsuz etiket="bakiye" />
                    </td>
                    <td className="text-right">
                      <Buton
                        tur="sessiz"
                        onClick={() =>
                          setSecili((s) => (s === k.id ? null : k.id))
                        }
                        aria-expanded={secili === k.id}
                      >
                        {secili === k.id ? "Kapat" : "Detay"}
                      </Buton>
                    </td>
                  </tr>
                ))}
              </TabloGovde>
            </Tablo>
            <Sayfalayici
              sayfa={sorgu.data?.sayfa ?? sayfa}
              boyut={sorgu.data?.boyut ?? BOYUT}
              toplam={sorgu.data?.toplam ?? 0}
              degistir={setSayfa}
            />
          </SorguDurumu>
        </section>

        <section aria-label="Kişi detayı">
          {secili ? (
            <KisiDetayPaneli
              kisiId={secili}
              kapat={() => setSecili(null)}
              degisti={() => void sorgu.refetch()}
            />
          ) : (
            <Kart baslik="Detay">
              <BosDurum
                baslik="Bir kişi seçin"
                aciklama="Soldaki listeden bir satırın 'Detay' düğmesine basın; cüzdanlar, kartlar, kısıtlar ve son hareketler burada açılır."
              />
            </Kart>
          )}
        </section>
      </div>
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------
// Yeni kişi
// ---------------------------------------------------------------------------

function YeniKisiFormu({
  kapat,
  eklendi,
}: {
  kapat: () => void;
  eklendi: () => void;
}) {
  const [ad, setAd] = useState("");
  const [soyad, setSoyad] = useState("");
  const [sinif, setSinif] = useState("");
  const [numara, setNumara] = useState("");
  const [kimlikNo, setKimlikNo] = useState("");
  const [parola, setParola] = useState("");
  const [tur, setTur] = useState(String(KisiTuru.OGRENCI));
  const [alerji, setAlerji] = useState("");
  const [fotoMedyaId, setFotoMedyaId] = useState<string | null>(null);
  const [fotoRizasi, setFotoRizasi] = useState(false);
  const [veliAcik, setVeliAcik] = useState(false);
  const [veliAd, setVeliAd] = useState("");
  const [veliSoyad, setVeliSoyad] = useState("");
  const [veliTelefon, setVeliTelefon] = useState("");
  const [veliEposta, setVeliEposta] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const kaydet = useMutation({
    mutationFn: () =>
      apiYaz<{ id: string; yukleme_referansi: string }>("POST", "/kisi", {
        ad: ad.trim(),
        soyad: soyad.trim(),
        sinif: sinif.trim() || null,
        numara: numara.trim() || null,
        kimlik_no: kimlikNo.trim() || null,
        parola: parola.trim() || null,
        tur: Number.parseInt(tur, 10),
        alerji_notu: alerji.trim() || null,
        foto_medya_id: fotoMedyaId,
        foto_rizasi: fotoRizasi,
        veli: veliAcik
          ? {
              ad: veliAd.trim(),
              soyad: veliSoyad.trim(),
              telefon: veliTelefonKanonik ?? "",
              eposta: veliEposta.trim() || null,
            }
          : null,
      }),
    onSuccess: eklendi,
  });

  const adHatasi = gonderildi && !ad.trim() ? "Ad zorunludur." : undefined;
  const soyadHatasi =
    gonderildi && !soyad.trim() ? "Soyad zorunludur." : undefined;
  // ADR-17 / §12.3: fotoğraf opsiyoneldir ama yüklendiyse açık rıza ŞART.
  const rizaHatasi =
    fotoMedyaId && !fotoRizasi
      ? "Fotoğraf için veli/kişi açık rızası işaretlenmelidir."
      : undefined;
  const veliTelefonKanonik = telefonNormalize(veliTelefon);
  const veliTelefonHatasi =
    veliAcik && gonderildi && !veliTelefonKanonik
      ? `Eksik numara. Örnek: ${TELEFON_ORNEK}`
      : undefined;

  return (
    <Kart baslik="Yeni kişi">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!ad.trim() || !soyad.trim() || rizaHatasi) return;
          if (veliAcik && !veliTelefonKanonik) return;
          kaydet.mutate();
        }}
      >
        <div className="grid gap-3 md:grid-cols-3">
          <Alan
            etiket="Ad"
            value={ad}
            onChange={(e) => setAd(e.target.value)}
            {...(adHatasi ? { hata: adHatasi } : {})}
            required
          />
          <Alan
            etiket="Soyad"
            value={soyad}
            onChange={(e) => setSoyad(e.target.value)}
            {...(soyadHatasi ? { hata: soyadHatasi } : {})}
            required
          />
          <Secim
            etiket="Tür"
            value={tur}
            onChange={(e) => setTur(e.target.value)}
            secenekler={[
              { deger: String(KisiTuru.OGRENCI), metin: "Öğrenci" },
              { deger: String(KisiTuru.PERSONEL), metin: "Personel" },
              { deger: String(KisiTuru.MISAFIR), metin: "Misafir" },
            ]}
          />
          <Alan
            etiket="Sınıf / departman"
            value={sinif}
            onChange={(e) => setSinif(e.target.value)}
            ipucu="Örnek: 5-A"
          />
          <Alan
            etiket="Numara"
            value={numara}
            onChange={(e) => setNumara(e.target.value)}
          />
          <Alan
            etiket="Kimlik no"
            value={kimlikNo}
            maxLength={20}
            inputMode="numeric"
            autoComplete="off"
            onChange={(e) => setKimlikNo(e.target.value)}
            ipucu="İsteğe bağlı"
          />
          <ParolaAlani deger={parola} degistir={setParola} kilitli={kaydet.isPending} />
        </div>

        <FotografSecici
          ad={ad}
          soyad={soyad}
          medyaId={fotoMedyaId}
          degistir={setFotoMedyaId}
        />
        {rizaHatasi && (
          <p role="alert" className="m-0 text-xs text-hata">
            {rizaHatasi}
          </p>
        )}

        <OnayKutusu
          etiket="Fotoğraf için açık rıza alındı"
          aciklama="Rızasız fotoğraf KVKK açısından işlenemez; fotoğraf seçildiyse bu kutu zorunludur."
          isaretli={fotoRizasi}
          degistir={setFotoRizasi}
        />

        <MetinAlani
          etiket="Alerji notu"
          value={alerji}
          onChange={(e) => setAlerji(e.target.value)}
          ipucu="Özel nitelikli kişisel veridir; yalnızca gerekli olanı yazın."
          rows={2}
        />

        <div className="border-t border-cetvel pt-3">
          <OnayKutusu
            etiket="Veli bilgisi ekle"
            aciklama="Veli panelinden bakiye takibi ve kart blokajı için gereklidir."
            isaretli={veliAcik}
            degistir={setVeliAcik}
          />
          {veliAcik && (
            <div className="mt-3 grid gap-3 md:grid-cols-4">
              <Alan
                etiket="Veli adı"
                value={veliAd}
                onChange={(e) => setVeliAd(e.target.value)}
              />
              <Alan
                etiket="Veli soyadı"
                value={veliSoyad}
                onChange={(e) => setVeliSoyad(e.target.value)}
              />
              <TelefonAlani
                etiket="Veli telefonu"
                value={veliTelefon}
                onChange={setVeliTelefon}
                {...(veliTelefonHatasi ? { hata: veliTelefonHatasi } : {})}
              />
              <Alan
                etiket="Veli e-postası"
                value={veliEposta}
                onChange={(e) => setVeliEposta(e.target.value)}
                type="email"
              />
            </div>
          )}
        </div>

        <IslemDurumu hata={kaydet.error} />

        <div className="flex flex-wrap gap-2">
          <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
            Kişiyi kaydet
          </Buton>
          <Buton type="button" tur="ikincil" onClick={kapat}>
            Vazgeç
          </Buton>
          <p className="m-0 self-center text-xs text-murekkep-3">
            Kayıt ile birlikte kantin cüzdanı ve yükleme referans kodu otomatik
            oluşur.
          </p>
        </div>
      </form>
    </Kart>
  );
}

// ---------------------------------------------------------------------------
// Detay + düzenleme
// ---------------------------------------------------------------------------

function KisiDetayPaneli({
  kisiId,
  kapat,
  degisti,
}: {
  kisiId: string;
  kapat: () => void;
  degisti: () => void;
}) {
  const istemci = useQueryClient();
  const sorgu = useQuery({
    queryKey: ["kisi", kisiId],
    queryFn: () => apiOku<KisiDetayi>(`/kisi/${kisiId}`),
  });
  const [duzenle, setDuzenle] = useState(false);
  const [veliEkle, setVeliEkle] = useState(false);
  const [pasifOnayi, setPasifOnayi] = useState(false);

  const pasifeAl = useMutation({
    mutationFn: () =>
      apiYaz<{
        id: string;
        havuza_donen_kartlar: string[];
        depozito_iadesi: string;
        iade_bekleyen_bakiye: string;
      }>("POST", `/kisi/${kisiId}/pasif`),
    onSuccess: () => {
      setPasifOnayi(false);
      void istemci.invalidateQueries({ queryKey: ["kisi", kisiId] });
      degisti();
    },
  });

  const k = sorgu.data;

  return (
    <Kart
      baslik="Kişi detayı"
      ustBilgi={
        <Buton tur="sessiz" onClick={kapat}>
          Kapat
        </Buton>
      }
    >
      <SorguDurumu
        yukleniyor={sorgu.isPending}
        hata={sorgu.error}
        bos={!k}
        bosBaslik="Kayıt bulunamadı"
        yenile={() => void sorgu.refetch()}
      >
        {k && (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <KisiAvatari kisi={k} boyut={64} />
              <div className="min-w-0">
                <h3 className="m-0 text-xl">
                  {k.ad} {k.soyad}
                </h3>
                <p className="m-0 text-sm text-murekkep-2">
                  {KISI_TURU_ADI[k.tur] ?? "Bilinmiyor"}
                  {k.sinif ? ` · ${k.sinif}` : ""}
                  {k.numara ? ` · No ${k.numara}` : ""}
                </p>
                <p className="m-0 mt-1 text-xs text-murekkep-3">
                  Yükleme referansı:{" "}
                  <span className="rakam font-mono text-murekkep-2">
                    {k.yukleme_referansi}
                  </span>
                </p>
                {k.kimlik_no && (
                  <p className="m-0 mt-1 text-xs text-murekkep-3">
                    Kimlik no:{" "}
                    <span className="rakam font-mono text-murekkep-2">
                      {k.kimlik_no}
                    </span>
                  </p>
                )}
                {!k.aktif && (
                  <p className="m-0 mt-1">
                    <Rozet tur="uyari">pasif</Rozet>
                  </p>
                )}
              </div>
            </div>

            {k.alerji_notu && (
              <Uyari tur="uyari" baslik="Alerji notu">
                {k.alerji_notu}
              </Uyari>
            )}

            {k.tur === KisiTuru.OGRENCI && !duzenle ? (
              <VeliParolasi
                key={`${k.id}:${k.parola ?? ""}`}
                kisiId={k.id}
                mevcutParola={k.parola}
                kimlikNo={k.kimlik_no}
                kaydedildi={() => {
                  void istemci.invalidateQueries({ queryKey: ["kisi", kisiId] });
                  degisti();
                }}
              />
            ) : k.parola && (
              <section aria-label="Parola">
                <h4 className="m-0 mb-1 text-sm font-semibold text-murekkep-3">
                  Parola
                </h4>
                <p className="m-0 mb-1 text-xs text-murekkep-3">
                  Veliye iletilecek erişim kodu; tekrar sorulursa buradan
                  okunur.
                </p>
                <SirKopyala deger={k.parola} etiket="Parolayı" />
              </section>
            )}

            <section aria-label="Cüzdanlar">
              <h4 className="m-0 mb-2 text-sm font-semibold text-murekkep-3">
                Cüzdanlar
              </h4>
              {k.cuzdanlar.length === 0 ? (
                <p className="m-0 text-sm text-murekkep-3">Cüzdan yok.</p>
              ) : (
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                  {k.cuzdanlar.map((c) => (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-3 border-b border-cetvel py-1 last:border-0"
                    >
                      <span className="text-sm text-murekkep-2">
                        {CUZDAN_ADI[c.tur] ?? "Cüzdan"}
                      </span>
                      <Tutar deger={c.bakiye} yonsuz etiket="bakiye" boy="lg" />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-label="Kartlar">
              <h4 className="m-0 mb-2 text-sm font-semibold text-murekkep-3">
                Kartlar
              </h4>
              {k.kartlar.length === 0 ? (
                <p className="m-0 text-sm text-murekkep-3">
                  Bu kişiye kart eşleştirilmemiş. Kartlar ekranından
                  eşleştirebilirsiniz.
                </p>
              ) : (
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {k.kartlar.map((kt) => (
                    <li
                      key={kt.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-cetvel px-3 py-2"
                    >
                      <span className="rakam font-mono text-sm">{kt.uid}</span>
                      <span className="flex items-center gap-2">
                        <Rozet
                          tur={
                            kt.durum === KartDurumu.AKTIF
                              ? "onay"
                              : kt.durum === KartDurumu.BLOKE
                                ? "hata"
                                : "notr"
                          }
                        >
                          {KART_DURUM_ADI[kt.durum] ?? "Bilinmiyor"}
                        </Rozet>
                        {kt.bloke_sebebi && (
                          <span className="text-xs text-murekkep-3">
                            {kt.bloke_sebebi}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {k.kisitlar.length > 0 && (
              <section aria-label="Kısıtlar">
                <h4 className="m-0 mb-2 text-sm font-semibold text-murekkep-3">
                  Kısıtlar
                </h4>
                <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
                  {k.kisitlar.map((ks) => (
                    <li key={ks.id} className="flex items-center gap-2">
                      <Rozet tur={ks.kaynak === 2 ? "simit" : "bilgi"}>
                        {ks.kaynak === 2 ? "okul" : "veli"}
                      </Rozet>
                      <span className="text-murekkep-2">
                        {KISIT_ADI[ks.tip] ?? "Kısıt"}
                        {ks.alerjen ? `: ${ks.alerjen}` : ""}
                      </span>
                      {ks.deger && (
                        <Tutar
                          deger={ks.deger}
                          yonsuz
                          boy="sm"
                          etiket="limit"
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section aria-label="Veliler">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h4 className="m-0 text-sm font-semibold text-murekkep-3">
                  Veliler
                </h4>
                {!veliEkle && (
                  <Buton tur="sessiz" onClick={() => setVeliEkle(true)}>
                    Veli ekle
                  </Buton>
                )}
              </div>
              {k.veliler.length === 0 && !veliEkle && (
                <p className="m-0 text-sm text-murekkep-3">
                  Veli kaydı yok. Veli panelinden bakiye takibi ve kart blokajı
                  için ekleyin.
                </p>
              )}
              {veliEkle && (
                <VeliFormu kisiId={kisiId} kapat={() => setVeliEkle(false)} />
              )}
              {k.veliler.length > 0 && (
                <ul className="m-0 flex list-none flex-col gap-2 p-0 text-sm text-murekkep-2">
                  {k.veliler.map((v) => (
                    <VeliSatiri key={v.id} kisiId={kisiId} veli={v} />
                  ))}
                </ul>
              )}
            </section>

            <section aria-label="Son hareketler">
              <h4 className="m-0 mb-2 text-sm font-semibold text-murekkep-3">
                Son hareketler
              </h4>
              {k.son_hareketler.length === 0 ? (
                <p className="m-0 text-sm text-murekkep-3">
                  Henüz bakiye hareketi yok.
                </p>
              ) : (
                <Tablo>
                  <TabloBaslik>
                    <th scope="col">Zaman</th>
                    <th scope="col">İşlem</th>
                    <th scope="col">Tutar</th>
                  </TabloBaslik>
                  <TabloGovde>
                    {k.son_hareketler.map((h) => (
                      <tr key={h.id}>
                        <td className="whitespace-nowrap text-xs text-murekkep-3">
                          {tarihSaatGoster(h.olusma_zamani)}
                        </td>
                        <td>
                          {HAREKET_TIPI_ADI[h.tip] ?? "Hareket"}
                          {h.nokta_ad && (
                            <span className="block text-xs text-murekkep-3">
                              {h.nokta_ad}
                            </span>
                          )}
                        </td>
                        <td className="text-right">
                          <Tutar deger={h.tutar} isaretiGoster />
                        </td>
                      </tr>
                    ))}
                  </TabloGovde>
                </Tablo>
              )}
            </section>

            <div className="flex flex-wrap gap-2 border-t border-cetvel pt-3">
              <Buton
                tur="ikincil"
                onClick={() => setDuzenle((d) => !d)}
                aria-expanded={duzenle}
              >
                {duzenle ? "Düzenlemeyi kapat" : "Düzenle"}
              </Buton>
              {k.aktif && !pasifOnayi && (
                <Buton tur="tehlike" onClick={() => setPasifOnayi(true)}>
                  Pasife al (mezuniyet / ayrılış)
                </Buton>
              )}
            </div>

            {pasifOnayi && (
              <TehlikeliOnay
                soru={`${k.ad} ${k.soyad} pasife alınsın mı?`}
                aciklama={
                  <>
                    <p className="m-0">
                      Kişi pasife alınır, aktif kartları havuza döner ve varsa
                      kart depozitosu iade edilir.
                    </p>
                    <p className="m-0 mt-1">
                      <strong>Kantin bakiyesi burada dokunulmaz.</strong> Kalan
                      bakiye iade/devir sürecine girer; bu süreç toplu ve ayrı
                      bir işlemdir.
                    </p>
                  </>
                }
                onayMetni="Pasife al"
                onayla={() => pasifeAl.mutate()}
                vazgec={() => setPasifOnayi(false)}
                yukleniyor={pasifeAl.isPending}
                cocuk={<IslemDurumu hata={pasifeAl.error} />}
              />
            )}

            {pasifeAl.data && (
              <Uyari tur="onay" baslik="Kişi pasife alındı">
                <p className="m-0">
                  Havuza dönen kart:{" "}
                  <span className="rakam font-mono">
                    {pasifeAl.data.havuza_donen_kartlar.length}
                  </span>{" "}
                  · İade edilen depozito:{" "}
                  <Tutar
                    deger={pasifeAl.data.depozito_iadesi}
                    yonsuz
                    boy="sm"
                  />
                </p>
                <p className="m-0">
                  İade sürecine giren bakiye:{" "}
                  <Tutar
                    deger={pasifeAl.data.iade_bekleyen_bakiye}
                    yonsuz
                    boy="sm"
                  />
                </p>
              </Uyari>
            )}

            {duzenle && (
              <KisiDuzenleFormu
                kisi={k}
                kaydedildi={() => {
                  setDuzenle(false);
                  void istemci.invalidateQueries({
                    queryKey: ["kisi", kisiId],
                  });
                  degisti();
                }}
              />
            )}
          </div>
        )}
      </SorguDurumu>
    </Kart>
  );
}

function KisiDuzenleFormu({
  kisi,
  kaydedildi,
}: {
  kisi: KisiDetayi;
  kaydedildi: () => void;
}) {
  const [ad, setAd] = useState(kisi.ad);
  const [soyad, setSoyad] = useState(kisi.soyad);
  const [sinif, setSinif] = useState(kisi.sinif ?? "");
  const [numara, setNumara] = useState(kisi.numara ?? "");
  const [kimlikNo, setKimlikNo] = useState(kisi.kimlik_no ?? "");
  // Mevcut parola görünür ve düzenlenir; boş bırakmak kaldırır.
  const [parola, setParola] = useState(kisi.parola ?? "");
  const [tur, setTur] = useState(String(kisi.tur));
  const [alerji, setAlerji] = useState(kisi.alerji_notu ?? "");
  const [fotoMedyaId, setFotoMedyaId] = useState<string | null>(
    kisi.foto_medya_id,
  );
  const [fotoRizasi, setFotoRizasi] = useState(kisi.foto_rizasi);

  const kaydet = useMutation({
    mutationFn: () =>
      apiYaz<KisiDetayi>("PATCH", `/kisi/${kisi.id}`, {
        ad: ad.trim(),
        soyad: soyad.trim(),
        sinif: sinif.trim() || null,
        numara: numara.trim() || null,
        kimlik_no: kimlikNo.trim() || null,
        tur: Number.parseInt(tur, 10),
        alerji_notu: alerji.trim() || null,
        foto_medya_id: fotoMedyaId,
        foto_rizasi: fotoRizasi,
        parola: parola.trim() || null,
      }),
    onSuccess: kaydedildi,
  });

  const rizaHatasi =
    fotoMedyaId && !fotoRizasi
      ? "Fotoğraf için açık rıza işaretlenmelidir."
      : undefined;

  return (
    <form
      className="flex flex-col gap-3 rounded-md border border-cetvel bg-kagit-2 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (rizaHatasi || !ad.trim() || !soyad.trim()) return;
        kaydet.mutate();
      }}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <Alan etiket="Ad" value={ad} onChange={(e) => setAd(e.target.value)} />
        <Alan
          etiket="Soyad"
          value={soyad}
          onChange={(e) => setSoyad(e.target.value)}
        />
        <Alan
          etiket="Sınıf / departman"
          value={sinif}
          onChange={(e) => setSinif(e.target.value)}
        />
        <Alan
          etiket="Numara"
          value={numara}
          onChange={(e) => setNumara(e.target.value)}
        />
        <Secim
          etiket="Tür"
          value={tur}
          onChange={(e) => setTur(e.target.value)}
          secenekler={[
            { deger: String(KisiTuru.OGRENCI), metin: "Öğrenci" },
            { deger: String(KisiTuru.PERSONEL), metin: "Personel" },
            { deger: String(KisiTuru.MISAFIR), metin: "Misafir" },
          ]}
        />
        <Alan
          etiket="Kimlik no"
          value={kimlikNo}
          maxLength={20}
          inputMode="numeric"
          autoComplete="off"
          onChange={(e) => setKimlikNo(e.target.value)}
        />
        <ParolaAlani deger={parola} degistir={setParola} kilitli={kaydet.isPending} />
      </div>

      <FotografSecici
        ad={ad}
        soyad={soyad}
        medyaId={fotoMedyaId}
        eskiAdres={kisi.foto_url}
        degistir={setFotoMedyaId}
      />
      {rizaHatasi && (
        <p role="alert" className="m-0 text-xs text-hata">
          {rizaHatasi}
        </p>
      )}
      <OnayKutusu
        etiket="Fotoğraf için açık rıza alındı"
        isaretli={fotoRizasi}
        degistir={setFotoRizasi}
      />
      <MetinAlani
        etiket="Alerji notu"
        value={alerji}
        onChange={(e) => setAlerji(e.target.value)}
        rows={2}
      />
      <IslemDurumu hata={kaydet.error} />
      <div>
        <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
          Değişiklikleri kaydet
        </Buton>
      </div>
    </form>
  );
}

/**
 * Kişi avatarı — fotoğraf artık bir adres değil, YETKİLİ bir istekle alınan
 * içerik. Bu yüzden `Avatar`a doğrudan `foto_url` verilemez; içerik önce
 * çekilip sekmeye özel geçici bir `blob:` adresine çevrilir.
 *
 * `foto_url` yalnızca 0015 öncesi kayıtlar için yedek olarak geçilir.
 */
function KisiAvatari({ kisi, boyut }: { kisi: KisiDetayi; boyut: number }) {
  const goruntu = useMedyaGoruntusu(kisi.foto_medya_id, kisi.foto_url);
  return (
    <Avatar
      ad={kisi.ad}
      soyad={kisi.soyad}
      fotoUrl={goruntu.url}
      boyut={boyut}
    />
  );
}

// ---------------------------------------------------------------------------
// Veli satırı
// ---------------------------------------------------------------------------

/**
 * Veli, panele öğrencinin kaydındaki kimlik no + parola ile girer (0027);
 * bu satırda giriş bilgisi YOKTUR. Telefon ve e-posta bildirim/iletişim
 * içindir; kimlik no ile ilk girişte kurulan hesabın telefonu boş olabilir.
 */
function VeliSatiri({
  kisiId,
  veli,
}: {
  kisiId: string;
  veli: KisiDetayi["veliler"][number];
}) {
  const [duzenle, setDuzenle] = useState(false);

  if (duzenle) {
    return (
      <li>
        <VeliFormu
          kisiId={kisiId}
          veli={veli}
          kapat={() => setDuzenle(false)}
        />
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-center gap-2">
      <span>
        {veli.ad} {veli.soyad}
        {veli.yakinlik ? ` (${veli.yakinlik})` : ""}
        {veli.telefon ? (
          <>
            {" · "}
            <span className="rakam font-mono">
              {telefonGoster(veli.telefon)}
            </span>
          </>
        ) : (
          <span className="text-murekkep-3"> · telefon yok</span>
        )}
      </span>
      <Buton tur="sessiz" onClick={() => setDuzenle(true)}>
        Düzenle
      </Buton>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Veli ekle / düzenle
// ---------------------------------------------------------------------------

/**
 * Veli bilgisi kişi kaydından bağımsız olarak her zaman eklenip
 * güncellenebilir. `veli` verilirse düzenleme (PATCH), verilmezse ekleme
 * (POST) yapılır; aynı telefonlu veli okulda zaten varsa sunucu yeni hesap
 * açmaz, mevcut hesabı bu çocuğa bağlar.
 */
function VeliFormu({
  kisiId,
  veli,
  kapat,
}: {
  kisiId: string;
  veli?: KisiDetayi["veliler"][number];
  kapat: () => void;
}) {
  const istemci = useQueryClient();
  const [ad, setAd] = useState(veli?.ad ?? "");
  const [soyad, setSoyad] = useState(veli?.soyad ?? "");
  // Kanonik "5321234567" değil, maskeli "0532 123 45 67" ile başlasın.
  const [telefon, setTelefon] = useState(
    veli ? telefonGoster(veli.telefon) : "",
  );
  const [eposta, setEposta] = useState(veli?.eposta ?? "");
  const [yakinlik, setYakinlik] = useState(veli?.yakinlik ?? "");
  const [gonderildi, setGonderildi] = useState(false);

  const telefonKanonik = telefonNormalize(telefon);
  const adHatasi = gonderildi && !ad.trim() ? "Ad zorunludur." : undefined;
  const soyadHatasi =
    gonderildi && !soyad.trim() ? "Soyad zorunludur." : undefined;
  const telefonHatasi =
    gonderildi && !telefonKanonik
      ? `Eksik numara. Örnek: ${TELEFON_ORNEK}`
      : undefined;

  const kaydet = useMutation({
    mutationFn: () => {
      const govde = {
        ad: ad.trim(),
        soyad: soyad.trim(),
        telefon: telefonKanonik ?? "",
        eposta: eposta.trim() || null,
        yakinlik: yakinlik.trim() || null,
      };
      return veli
        ? apiYaz("PATCH", `/kisi/${kisiId}/veli/${veli.id}`, govde)
        : apiYaz("POST", `/kisi/${kisiId}/veli`, govde);
    },
    onSuccess: () => {
      void istemci.invalidateQueries({ queryKey: ["kisi", kisiId] });
      kapat();
    },
  });

  return (
    <form
      className="flex flex-col gap-3 rounded-md border border-cetvel bg-kagit-2 p-3"
      aria-label={veli ? "Veliyi düzenle" : "Veli ekle"}
      onSubmit={(e) => {
        e.preventDefault();
        setGonderildi(true);
        if (!ad.trim() || !soyad.trim() || !telefonKanonik) return;
        kaydet.mutate();
      }}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <Alan
          etiket="Veli adı"
          value={ad}
          onChange={(e) => setAd(e.target.value)}
          {...(adHatasi ? { hata: adHatasi } : {})}
          required
        />
        <Alan
          etiket="Veli soyadı"
          value={soyad}
          onChange={(e) => setSoyad(e.target.value)}
          {...(soyadHatasi ? { hata: soyadHatasi } : {})}
          required
        />
        <TelefonAlani
          etiket="Veli telefonu"
          value={telefon}
          onChange={setTelefon}
          {...(telefonHatasi ? { hata: telefonHatasi } : {})}
        />
        <Alan
          etiket="Veli e-postası"
          value={eposta}
          onChange={(e) => setEposta(e.target.value)}
          type="email"
        />
        <Alan
          etiket="Yakınlık"
          value={yakinlik}
          onChange={(e) => setYakinlik(e.target.value)}
          ipucu="Örnek: Anne, Baba, Vasi"
        />
      </div>
      {veli && (
        <p className="m-0 text-xs text-murekkep-3">
          Telefon, velinin panele giriş numarasıdır; değişirse veliye bildirin.
          Ad ve telefon velinin bütün çocuklarında güncellenir.
        </p>
      )}
      <IslemDurumu hata={kaydet.error} />
      <div className="flex flex-wrap gap-2">
        <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
          {veli ? "Veliyi kaydet" : "Veli ekle"}
        </Buton>
        <Buton type="button" tur="sessiz" onClick={kapat}>
          Vazgeç
        </Buton>
      </div>
    </form>
  );
}
